import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMeasure } from 'react-use';

import {
  Box,
  Flex,
  useBreakpointValue,
  useDisclosure,
} from '@frontend/legacy-components';

import { PageTitle } from 'components/PageTitle';

import { MicroAppHeader } from 'containers/Layouts/DashboardLayout/MicroAppHeader';

import CADViewerInfo from 'modules/common/viewer/CADViewerInfo';
import CADViewerTools from 'modules/common/viewer/CADViewerTools';
import Viewer from 'modules/common/viewer/Viewer';
import { getActiveFile } from 'modules/common/viewer/data/reducer';
import { useCanDownloadPdf } from 'modules/common/viewer/hooks/featureConfig/useCanDownloadPdf';
import { useIsActiveFile3DRenderable } from 'modules/common/viewer/hooks/useActiveViewerKind';
import {
  WebViewerContextProvider,
  useWebViewerContext,
} from 'modules/common/viewer/pdfViewer/WebViewerContext';
import {
  usePrintUnitsFeature,
  useUnitsPicker,
} from 'modules/common/viewer/pdfViewer/printUnitsFeature';
import { buildStandaloneViewerConfig } from 'modules/common/viewer/pdfViewer/viewerConfig';
import { ExtractedTableProvider } from 'modules/extractedTables/ExtractedTableContext';
import { ExtractedTableModalContainer } from 'modules/extractedTables/ExtractedTableModalContainer';
import { SelectedTableProvider } from 'modules/extractedTables/SelectedTableContext';
import { useCanEditPartInQuote } from 'modules/quotePartViewer/hooks/useCanEditPartInQuote';

import { UnitSystemProvider } from 'utils/measurement/context';

import FileViewerCadTree from './components/FileViewerCadTree';
import { FileViewerHeader } from './components/FileViewerHeader';
import { UpdateItarModal } from './components/UpdateItarModal';
import { getActiveRootFileName } from './data/reducer';
import { useRefreshStaleQueriesOnMount } from './hooks/useRefreshStaleQueriesOnMount';
import { SfvPdfInteractionsProvider } from './hooks/useSfvPdfInteractions';

const FileViewerWrapped = () => {
  const { instance } = useWebViewerContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile = useBreakpointValue(
    {
      base: true,
      tablet: true,
      desktop: false,
    },
    { ssr: false },
  );
  const rootFileName = useSelector(getActiveRootFileName);
  // The CAD chrome (tree, toolbar, info overlays) belongs to the CAD viewer, so
  // it is shown exactly when the CAD viewer is the resolved viewer.
  const shouldShowCAD = useIsActiveFile3DRenderable();
  const { canEdit: canEditPart, disabledReason: canEditPartDisabledReason } =
    useCanEditPartInQuote();
  const [cadViewerRegion, { height: cadViewerRegionHeight = 1 }] =
    useMeasure<HTMLDivElement>();

  useRefreshStaleQueriesOnMount();

  // PDF Viewer Features
  const { printUnits, handleUpdatePrintUnits } = usePrintUnitsFeature();
  useUnitsPicker({
    instance,
    printUnits,
    onUpdatePrintUnits: handleUpdatePrintUnits,
    canUpdatePrintUnits: canEditPart,
    disabledTooltip: canEditPartDisabledReason,
  });

  const canDownloadPdf = useCanDownloadPdf();
  const viewerConfig = useMemo(
    () => buildStandaloneViewerConfig({ canDownloadPdf }),
    [canDownloadPdf],
  );

  //Note: FileViewerCadTree receives parentHeight so it knows the maxHeight of the floating cad tree
  return (
    <Flex
      direction="column"
      overflow="hidden"
      position="fixed"
      height="100%"
      width="100%"
    >
      <ExtractedTableModalContainer />
      <PageTitle title={rootFileName} />
      <MicroAppHeader />
      <FileViewerHeader onOpen={onOpen} />
      <UpdateItarModal isOpen={isOpen} onClose={onClose} />
      <Flex height="100%" width="100%" overflowX="hidden">
        <Box
          background="linear-gradient(150deg, #fff 0%, #cccccc 100%)"
          flexGrow={1}
          overflow="hidden"
          position="relative"
          ref={cadViewerRegion}
        >
          {shouldShowCAD && (
            <>
              <FileViewerCadTree
                p={1.5}
                pb={isMobile ? 0 : undefined}
                w={isMobile ? '100%' : undefined}
                parentHeight={cadViewerRegionHeight}
              />
              <Box
                position="absolute"
                top={isMobile ? 7 : 0}
                zIndex={2}
                width="100%"
                overflow="visible"
              >
                <CADViewerTools />
              </Box>
              <CADViewerInfo />
            </>
          )}
          <Viewer config={viewerConfig} leftOffset="300px" />
        </Box>
      </Flex>
    </Flex>
  );
};

const FileViewer = () => {
  const activeFile = useSelector(getActiveFile);
  const partFileUuids = useMemo(
    () => (activeFile?.uuid ? [activeFile.uuid] : []),
    [activeFile],
  );
  return (
    <UnitSystemProvider>
      <WebViewerContextProvider>
        <ExtractedTableProvider partFileUuids={partFileUuids}>
          <SelectedTableProvider>
            <SfvPdfInteractionsProvider>
              <FileViewerWrapped />
            </SfvPdfInteractionsProvider>
          </SelectedTableProvider>
        </ExtractedTableProvider>
      </WebViewerContextProvider>
    </UnitSystemProvider>
  );
};

export default FileViewer;
